/**
 * Lane C0-PROPS: the plains' props (C0_TOWN_LAYOUT §3.6 prop language; C0_SCENE_PLAINS §4): lantern posts, banner
 * poles, weapon racks, dummies, mats, guard stands, braziers, gate posts, warning signs, chests, a well, carts, fences,
 * an incense burner, children's toys, washing steps, fishing gear, benches, a hitching rail and trough, hay, barrels,
 * crates and signposts. Each is a small upright sprite anchored at its foot (`ax`, `ay`); with route C rotation they
 * stand upright like the trees (no faces to note). Made once each and shared (`propSprites`).
 */
import { hash2 } from '@nine/shared';
import { box, lantern, M, snap, wall, type Ramp } from '../buildings';
import type { Sprite } from '../scenery';
import { blob, clothBanner, K, propPix } from './parts';
import { fin, pole, stoneT, wood, type Pp } from './propBase';
import { MAKERS2, type PropId2 } from './props2';
import { MAKERS3, type PropId3 } from './props3';
import { MAKERS4, type PropId4 } from './props4';
import { MAKERS_CARPET, type PropIdCarpet } from './carpet';

/** A red lantern hung from a bracket on a post (≈ 62 px). */
function lanternPost(): Sprite {
  const p = propPix(30, 80, 12, 74);
  pole(p.P, p, 0, 0, 0.09, 0, 62);
  box(p.px, p.P, [-0.2, -0.2, 0.2, 0.2], 0, 4, stoneT);
  box(p.px, p.P, [0, -0.06, 0.55, 0.06], 56, 59, wood(0.1));
  lantern(p.px, p.P(0.5, 0, 56), 3);
  return fin(p);
}

/** A stone lantern (石灯籠, ≈ 34 px): base, shaft, a lit fire box, a curled cap. */
function stoneLantern(): Sprite {
  const p = propPix(26, 48, 13, 42);
  box(p.px, p.P, [-0.3, -0.3, 0.3, 0.3], 0, 5, stoneT);
  box(p.px, p.P, [-0.12, -0.12, 0.12, 0.12], 5, 17, stoneT);
  box(p.px, p.P, [-0.26, -0.26, 0.26, 0.26], 17, 20, stoneT);
  box(p.px, p.P, [-0.2, -0.2, 0.2, 0.2], 20, 28, (u, v, l) => (((u | 0) % 5 > 1 && v > 21 && v < 27) ? snap(M.paper, 0.95, u | 0, v | 0, 0) : stoneT(u, v, l)));
  box(p.px, p.P, [-0.34, -0.34, 0.34, 0.34], 28, 31, stoneT);
  blob(p.px, p.ax, p.ay - 34, 3, 2, M.stone, 0.65);
  return fin(p);
}

/** A sect banner on a tall pole with a crossbar and a gold finial (≈ 104 px). */
function bannerPole(cloth: Ramp, mark: Ramp): Sprite {
  const p = propPix(30, 120, 8, 114);
  box(p.px, p.P, [-0.22, -0.22, 0.22, 0.22], 0, 6, stoneT);
  pole(p.P, p, 0, 0, 0.06, 6, 104, M.lacquer);
  const [tx, ty] = p.P(0, 0, 104);
  p.px.rect(tx - 1, ty - 4, 3, 4, M.gold[3]!);
  p.px.set(tx, ty - 5, M.gold[4]!);
  clothBanner(p.px, [tx + 2, ty + 6], 13, 52, cloth, mark);
  return fin(p);
}

/** A weapon rack (兵器架): a frame with spears, staffs, a halberd and a sword (≈ 46 px). */
function weaponRack(): Sprite {
  const p = propPix(70, 64, 22, 56);
  for (const x of [0, 1.2]) pole(p.P, p, x, 0, 0.06, 0, 34);
  box(p.px, p.P, [-0.05, -0.08, 1.25, 0.08], 30, 33, wood(0.1));
  box(p.px, p.P, [-0.05, -0.08, 1.25, 0.08], 8, 10, wood(0));
  for (let k = 0; k < 7; k++) {
    const [x, y] = p.P(0.1 + k * 0.17, 0.02, 0);
    const len = 42 + (k % 3) * 4;
    for (let t = 0; t < len; t++) p.px.set(x, y - t, t > len - 6 ? (k % 3 === 1 ? M.red[3]! : K.iron[4]!) : M.wood[k % 2 ? 2 : 3]!);
    if (k % 3 === 1) p.px.rect(x - 1, y - len + 4, 3, 2, M.red[2]!); // a red tassel under the blade
  }
  return fin(p);
}

/** A training dummy: a post with arms, wrapped in straw and rope (≈ 40 px). */
function dummy(v: number): Sprite {
  const p = propPix(34, 52, 17, 46);
  box(p.px, p.P, [-0.25, -0.25, 0.25, 0.25], 0, 3, (u, vv, l) => snap(M.wood, l - 0.1, u | 0, vv | 0));
  pole(p.P, p, 0, 0, 0.1, 3, 40, v ? M.wood : K.straw);
  for (let z = 10; z < 34; z += 5) box(p.px, p.P, [-0.13, -0.13, 0.13, 0.13], z, z + 1, (u, vv) => snap(K.straw, 0.35, u | 0, vv | 0, 0));
  // Arms: two pegs out to the front and one low.
  for (const [dx, dy, z] of [
    [0.3, 0.1, 30],
    [0.1, 0.3, 30],
    [0.25, 0.25, 18],
  ] as const) {
    const [ax, ay] = p.P(0, 0, z);
    const [bx, by] = p.P(dx, dy, z - 2);
    for (let k = 0; k <= 8; k++) {
      p.px.set(ax + (bx - ax) * (k / 8), ay + (by - ay) * (k / 8), M.wood[3]!);
      p.px.set(ax + (bx - ax) * (k / 8), ay + (by - ay) * (k / 8) + 1, M.wood[1]!);
    }
  }
  blob(p.px, p.ax, p.ay - 43, 4, 3, v ? M.wood : K.straw, 0.6);
  return fin(p);
}

/** A woven straw training mat, flat on the ground. */
function mat(): Sprite {
  const p = propPix(70, 40, 35, 20);
  box(p.px, p.P, [-1, -0.7, 1, 0.7], 0, 1, () => null, (u, v) => {
    const edge = Math.abs(u) > 14 || Math.abs(v) > 10;
    return snap(edge ? M.red : K.straw, 0.55 + (((u | 0) + (v | 0)) % 4 < 2 ? 0.08 : -0.06), u | 0, v | 0, 0);
  });
  return fin(p);
}

/** A guard's stand: a spear rack and a small lit brazier on a tripod (≈ 44 px). */
function guardStand(): Sprite {
  const p = propPix(52, 64, 18, 56);
  for (const x of [0, 0.6]) pole(p.P, p, x, 0, 0.05, 0, 26);
  box(p.px, p.P, [-0.05, -0.06, 0.65, 0.06], 22, 24, wood(0.1));
  for (let k = 0; k < 4; k++) {
    const [x, y] = p.P(0.06 + k * 0.17, 0, 0);
    for (let t = 0; t < 46; t++) p.px.set(x, y - t, t > 40 ? K.iron[4]! : M.wood[2 + (k % 2)]!);
    p.px.rect(x - 1, y - 40, 3, 2, M.red[3]!);
  }
  brazier(p, 1.2, 0.3);
  return fin(p);
}
/** A brazier on three legs with its fire, drawn into a prop at (x, y). */
export function brazier(p: Pp, x: number, y: number): void {
  const [bx, by] = p.P(x, y, 0);
  for (const dx of [-4, 0, 4]) for (let t = 0; t < 16; t++) p.px.set(bx + dx * (1 - t / 22), by - t, K.iron[1]!);
  blob(p.px, bx, by - 17, 6, 3, K.iron, 0.4);
  for (let k = 0; k < 14; k++) {
    const fx = bx + Math.round((hash2(k, 7) - 0.5) * 8);
    const fy = by - 19 - Math.round(hash2(k, 3) * 7);
    p.px.set(fx, fy, k % 3 ? M.red[4]! : M.gold[4]!);
  }
}

/** A brazier alone (≈ 26 px). */
function brazierProp(): Sprite {
  const p = propPix(24, 34, 12, 30);
  brazier(p, 0, 0);
  return fin(p);
}

/** A town-gate post: a squat post with a little tiled cap and a hanging lantern (≈ 58 px). */
function gatePost(): Sprite {
  const p = propPix(34, 74, 14, 68);
  box(p.px, p.P, [-0.3, -0.3, 0.3, 0.3], 0, 8, stoneT);
  pole(p.P, p, 0, 0, 0.13, 8, 40, M.lacquer);
  box(p.px, p.P, [-0.14, -0.14, 0.14, 0.14], 30, 33, (u, v, l) => snap(M.gold, l, u | 0, v | 0, 0));
  box(p.px, p.P, [-0.3, -0.3, 0.3, 0.3], 40, 43, (u, v, l) => snap(K.redTile, l, u | 0, v | 0, 0));
  blob(p.px, p.ax, p.ay - 46, 4, 2, K.redTile, 0.6);
  lantern(p.px, p.P(0.2, 0.2, 38), 2);
  return fin(p);
}

/** A warning sign (ป้ายเตือน) at a trailhead: a board on a post with the danger tier as red ticks (≈ 40 px). */
function warnSign(tier: number): Sprite {
  const p = propPix(34, 52, 17, 46);
  pole(p.P, p, 0, 0, 0.06, 0, 30);
  wall(p.px, p.P, 'left', 0.08, -0.55, 0.55, 22, 38, (u, v, l) => {
    const a = -0.55 * 16;
    if (u - a < 1.2 || 0.55 * 16 - u < 1.2 || v - 22 < 1.2 || 38 - v < 1.2) return snap(M.wood, l - 0.15, u | 0, v | 0, 0);
    const tick = Math.floor((u - a - 2) / 3.2);
    if (v > 24 && v < 29 && tick < tier && (u - a - 2) % 3.2 < 2) return M.red[3]!;
    if (v > 31 && v < 35 && (u | 0) % 3 !== 0) return M.dark[2]!;
    return snap(K.cream, l + 0.2, u | 0, v | 0, 0);
  });
  return fin(p);
}

/** A treasure chest: lacquered wood, gold corners and lock, iron bands (≈ 16 px). */
function chest(): Sprite {
  const p = propPix(36, 30, 18, 22);
  box(p.px, p.P, [-0.45, -0.3, 0.45, 0.3], 0, 10, (u, v, l) => ((u | 0) % 8 < 2 ? snap(M.gold, l + 0.1, u | 0, v | 0, 0) : snap(K.maroon, l + 0.15, u | 0, v | 0)));
  box(p.px, p.P, [-0.45, -0.3, 0.45, 0.3], 10, 15, (u, v, l) => snap(M.red, l, u | 0, v | 0), (u, v, l) => snap(M.red, l + 0.1 + ((u | 0) % 8 < 2 ? 0.2 : 0), u | 0, v | 0, 0));
  const [lx, ly] = p.P(0, 0.3, 9);
  p.px.rect(lx - 1, ly - 2, 3, 4, M.gold[4]!);
  return fin(p);
}

/** A well: a stone ring, a timber frame with a little roof, a bucket on its rope (≈ 50 px). */
function well(): Sprite {
  const p = propPix(48, 70, 24, 52);
  for (let b = -6; b <= 6; b++)
    for (let a = -14; a <= 14; a++) {
      const e = (a * a) / 196 + (b * b) / 36;
      if (e > 1) continue;
      p.px.set(p.ax + a, p.ay - 10 + b, e < 0.55 ? M.blue[1]! : snap(M.stone, 0.75 - b * 0.03, a, b, 0.2));
    }
  for (let b = 0; b < 10; b++)
    for (let a = -14; a <= 14; a++) {
      const y = Math.round(Math.sqrt(Math.max(0, 1 - (a * a) / 196)) * 6);
      p.px.set(p.ax + a, p.ay - 10 + y + b, snap(M.stone, 0.45 + a * 0.012 + ((b + (a >> 2)) % 4 === 0 ? -0.15 : 0), a, b, 0.2));
    }
  for (const a of [-12, 12]) for (let t = 0; t < 40; t++) p.px.rect(p.ax + a, p.ay - 10 - t, 2, 1, M.wood[a < 0 ? 2 : 3]!);
  p.px.rect(p.ax - 13, p.ay - 46, 28, 2, M.wood[2]!);
  for (let k = 0; k < 6; k++) p.px.rect(p.ax - 16 + k, p.ay - 47 - k, 34 - k * 2, 1, K.greyTile[3 - (k >> 1)]!);
  p.px.rect(p.ax, p.ay - 44, 1, 16, M.dark[2]!);
  blob(p.px, p.ax, p.ay - 26, 3, 3, M.wood, 0.55);
  return fin(p);
}

/** A hand cart loaded with sacks and a jar (≈ 30 px). */
function cart(): Sprite {
  const p = propPix(70, 48, 30, 38);
  box(p.px, p.P, [-0.8, -0.4, 0.8, 0.4], 10, 16, wood(0), wood(0.15));
  for (const [x, y] of [
    [-0.1, 0.45],
    [-0.1, -0.45],
  ] as const) {
    const [cx, cy] = p.P(x, y, 9);
    for (let b = -9; b <= 9; b++)
      for (let a = -4; a <= 4; a++) {
        const e = (a * a) / 16 + (b * b) / 81;
        if (e <= 1 && (e > 0.55 || a === 0 || b === 0)) p.px.set(cx + a, cy + b, M.wood[e > 0.75 ? 1 : 3]!);
      }
  }
  // Shafts to the front.
  const [sx, sy] = p.P(0.8, 0, 14);
  for (let k = 0; k < 18; k++) p.px.rect(sx + k, sy + Math.round(k * 0.5), 1, 2, M.wood[2]!);
  blob(p.px, ...p.P(-0.3, 0, 22), 6, 5, K.cream, 0.55);
  blob(p.px, ...p.P(0.3, 0.1, 21), 5, 4, K.straw, 0.55);
  blob(p.px, ...p.P(0.0, -0.2, 24), 4, 5, M.terra, 0.55);
  return fin(p);
}

/** A wooden fence section along x (`along` 'x') or y, 2 tiles long (≈ 20 px). */
function fence(along: 'x' | 'y'): Sprite {
  const p = propPix(80, 50, 40, 26);
  const L = 1;
  for (let k = 0; k <= 4; k++) {
    const t = -L + (2 * L * k) / 4;
    const [x, y] = along === 'x' ? [t, 0] : [0, t];
    pole(p.P, p, x, y, 0.05, 0, 20);
  }
  for (const z of [7, 15]) {
    if (along === 'x') box(p.px, p.P, [-L, -0.03, L, 0.03], z, z + 2, wood(0.05));
    else box(p.px, p.P, [-0.03, -L, 0.03, L], z, z + 2, wood(0.05));
  }
  return fin(p);
}

/** A bronze incense burner (香炉, ≈ 36 px): a tripod cauldron with lugs, three sticks burning, a curl of smoke. */
function incenseBurner(): Sprite {
  const p = propPix(40, 60, 20, 52);
  box(p.px, p.P, [-0.5, -0.5, 0.5, 0.5], 0, 6, stoneT);
  for (const dx of [-7, 0, 7]) for (let t = 0; t < 8; t++) p.px.set(p.ax + dx, p.ay - 6 - t, M.gold[1]!);
  blob(p.px, p.ax, p.ay - 20, 10, 7, M.gold, 0.5);
  blob(p.px, p.ax, p.ay - 26, 9, 2, M.dark, 0.5);
  for (const dx of [-11, 10]) p.px.rect(p.ax + dx, p.ay - 30, 2, 6, M.gold[2]!);
  for (let k = -1; k <= 1; k++) {
    p.px.rect(p.ax + k * 3, p.ay - 36, 1, 10, M.red[2]!);
    p.px.set(p.ax + k * 3, p.ay - 37, M.gold[4]!);
  }
  for (let t = 0; t < 14; t++) p.px.set(p.ax + Math.round(Math.sin(t / 2.5) * 2), p.ay - 39 - t, K.cream[2 + (t % 2)]!);
  return fin(p);
}

/** Children's play things: a hobby horse leaning on a post and spinning tops (v 0), a shuttlecock and chalk squares (v 1). */
function toys(v: number): Sprite {
  const p = propPix(64, 44, 32, 30);
  if (v === 0) {
    pole(p.P, p, 0, 0, 0.05, 0, 18);
    const [x, y] = p.P(0.1, 0.2, 0);
    for (let t = 0; t < 22; t++) p.px.set(x + Math.round(t * 0.35), y - t, M.wood[3]!);
    blob(p.px, x + 8, y - 23, 3, 2, M.red, 0.6);
    p.px.rect(x + 10, y - 24, 2, 1, M.dark[1]!);
    for (const [a, b, c] of [
      [0.8, 0.5, M.red],
      [1.1, 0.1, K.indigo],
      [0.6, -0.3, K.ochre],
    ] as const) {
      const [tx, ty] = p.P(a, b, 0);
      blob(p.px, tx, ty - 2, 3, 2, c, 0.6);
      p.px.set(tx, ty, M.dark[1]!);
    }
  } else {
    // Chalk squares (hopscotch) on the ground and a feathered shuttlecock.
    box(p.px, p.P, [-1.2, -0.3, 1.2, 0.3], 0, 1, () => null, (u, vv) => ((u | 0) % 8 === 0 || Math.abs(vv) > 3.5 ? K.cream[3]! : null));
    const [sx, sy] = p.P(0.6, 0.6, 0);
    p.px.rect(sx, sy - 2, 2, 2, M.gold[3]!);
    for (let k = 0; k < 4; k++) p.px.set(sx - 1 + k, sy - 4 - (k % 2), [M.red[3]!, K.cream[4]!, K.indigo[3]!, M.jade[4]!][k]!);
  }
  return fin(p);
}

/** Washing steps (ท่าซักผ้า): stone treads down to the water, a basket of clothes, a washboard. */
function washSteps(): Sprite {
  const p = propPix(70, 40, 30, 26);
  for (let k = 0; k < 3; k++) box(p.px, p.P, [-0.8 + k * 0.35, -0.8, -0.45 + k * 0.35, 0.8], 0, 9 - k * 3, stoneT, stoneT);
  blob(p.px, ...p.P(-0.9, 0.4, 14), 6, 4, K.straw, 0.55);
  blob(p.px, ...p.P(-0.9, 0.4, 18), 4, 2, K.cream, 0.7);
  box(p.px, p.P, [-0.3, 0.35, -0.1, 0.55], 6, 16, (u, v, l) => snap(M.deck, l + ((v | 0) % 2 ? -0.2 : 0.1), u | 0, v | 0, 0));
  return fin(p);
}

/** A drying line between two posts with cloths hung on it (≈ 40 px). */
function dryingLine(): Sprite {
  const p = propPix(80, 56, 38, 48);
  pole(p.P, p, -1, 0, 0.05, 0, 36);
  pole(p.P, p, 1, 0, 0.05, 0, 36);
  const [ax, ay] = p.P(-1, 0, 34);
  const [bx, by] = p.P(1, 0, 34);
  for (let k = 0; k <= 40; k++) p.px.set(ax + (bx - ax) * (k / 40), ay + (by - ay) * (k / 40) + Math.sin((k / 40) * Math.PI) * 2, M.dark[2]!);
  for (let c = 0; c < 4; c++) {
    const f = 0.15 + c * 0.22;
    const x = Math.round(ax + (bx - ax) * f);
    const y = Math.round(ay + (by - ay) * f + 2);
    const cloth = [K.indigo, K.cream, M.red, M.jade][c]!;
    for (let yy = 0; yy < 12; yy++) for (let xx = 0; xx < 6; xx++) p.px.set(x + xx, y + yy + Math.round(xx * 0.5), snap(cloth, 0.55 + xx * 0.04, xx, yy, 0));
  }
  return fin(p);
}

/** Fishing gear on a bank: a rod on its rest, a creel and a stool. */
function fishing(): Sprite {
  const p = propPix(60, 46, 22, 36);
  const [rx, ry] = p.P(0, 0, 6);
  for (let k = 0; k < 34; k++) p.px.set(rx + k, ry - Math.round(k * 0.55 - (k * k) / 140), M.wood[k < 10 ? 2 : 4]!);
  p.px.rect(rx - 1, ry, 2, 6, M.wood[2]!);
  blob(p.px, ...p.P(-0.5, 0.4, 6), 5, 5, K.straw, 0.55);
  box(p.px, p.P, [0.2, 0.4, 0.5, 0.7], 0, 7, wood(0), wood(0.1));
  return fin(p);
}

/** A plain bench along x (≈ 12 px). */
function bench(): Sprite {
  const p = propPix(56, 30, 28, 20);
  for (const x of [-0.7, 0.7]) box(p.px, p.P, [x - 0.06, -0.15, x + 0.06, 0.15], 0, 8, wood(-0.1));
  box(p.px, p.P, [-0.9, -0.18, 0.9, 0.18], 8, 11, wood(0), wood(0.15));
  return fin(p);
}

/** A hitching rail with a water trough (carriage stop, ≈ 22 px). */
function hitchRail(): Sprite {
  const p = propPix(80, 40, 40, 30);
  pole(p.P, p, -1, 0, 0.06, 0, 22);
  pole(p.P, p, 1, 0, 0.06, 0, 22);
  box(p.px, p.P, [-1.05, -0.04, 1.05, 0.04], 18, 21, wood(0.1));
  box(p.px, p.P, [-0.8, 0.25, 0.8, 0.6], 0, 8, wood(-0.05), (u, v) => snap(M.blue, 0.45 + ((u | 0) % 5 === 0 ? 0.15 : 0), u | 0, v | 0, 0));
  return fin(p);
}

/** Hay: a stack of bales (v 0) or a loose pile (v 1). */
function hay(v: number): Sprite {
  const p = propPix(48, 38, 24, 30);
  if (v === 0) {
    box(p.px, p.P, [-0.6, -0.3, 0.6, 0.3], 0, 9, (u, vv, l) => snap(K.straw, l + ((vv | 0) % 3 === 0 ? -0.15 : 0.05) + ((u | 0) % 12 === 0 ? -0.3 : 0), u | 0, vv | 0));
    box(p.px, p.P, [-0.3, -0.25, 0.4, 0.25], 9, 18, (u, vv, l) => snap(K.straw, l + ((vv | 0) % 3 === 0 ? -0.15 : 0.05), u | 0, vv | 0));
  } else {
    blob(p.px, p.ax, p.ay - 7, 13, 8, K.straw, 0.55);
    for (let k = 0; k < 20; k++) p.px.set(p.ax - 12 + Math.round(hash2(k, 2) * 24), p.ay - 6 - Math.round(hash2(k, 5) * 12), K.straw[5]!);
  }
  return fin(p);
}

/** Barrels and crates stacked (market goods, ≈ 26 px). */
function goodsStack(): Sprite {
  const p = propPix(56, 44, 28, 36);
  box(p.px, p.P, [-0.7, -0.3, -0.1, 0.3], 0, 12, (u, v, l) => snap(M.deck, l + ((u | 0) % 6 === 0 || (v | 0) % 6 === 0 ? -0.25 : 0), u | 0, v | 0, 0));
  box(p.px, p.P, [-0.6, -0.25, -0.15, 0.2], 12, 22, (u, v, l) => snap(M.deck, l + ((u | 0) % 6 === 0 || (v | 0) % 6 === 0 ? -0.25 : 0), u | 0, v | 0, 0));
  const [bx, by] = p.P(0.4, 0.1, 0);
  for (let b = -14; b <= 0; b++)
    for (let a = -5; a <= 5; a++) {
      const r = 5 - Math.abs(b + 7) * 0.08;
      if (Math.abs(a) > r) continue;
      const band = b === -3 || b === -11;
      p.px.set(bx + a, by + b, band ? K.iron[2]! : snap(M.wood, 0.5 + a * 0.06, a, b, 0));
    }
  blob(p.px, bx, by - 14, 5, 2, M.wood, 0.7);
  return fin(p);
}

/** A signpost at a fork: a post with two pointing boards (≈ 46 px). */
function signpost(): Sprite {
  const p = propPix(48, 60, 24, 54);
  pole(p.P, p, 0, 0, 0.06, 0, 44);
  for (const [z, dir] of [
    [38, 1],
    [30, -1],
  ] as const) {
    const [x, y] = p.P(0, 0, z);
    for (let k = 0; k < 16; k++)
      for (let t = 0; t < 5; t++)
        if (!(k > 13 && (t === 0 || t === 4))) p.px.set(x + dir * k, y + t - Math.round(k * 0.5 * dir), snap(M.deck, 0.6 - t * 0.05, k, t, 0));
  }
  return fin(p);
}

/** A sword stone (one of the nine round the stele): a blade planted in a mossy stone (≈ 30 px). */
function swordStone(empty: boolean): Sprite {
  const p = propPix(30, 46, 15, 38);
  blob(p.px, p.ax, p.ay - 5, 9, 5, M.stone, 0.55);
  blob(p.px, p.ax + 2, p.ay - 8, 5, 2, M.moss, 0.5);
  if (empty) {
    p.px.rect(p.ax - 1, p.ay - 9, 3, 2, M.dark[0]!);
    return fin(p);
  }
  const [x, y] = [p.ax, p.ay - 8];
  for (let t = 0; t < 18; t++) {
    p.px.set(x, y - t, K.iron[5]!);
    p.px.set(x + 1, y - t, K.iron[3]!);
  }
  p.px.rect(x - 3, y - 19, 8, 2, M.gold[3]!);
  for (let t = 0; t < 6; t++) p.px.rect(x, y - 21 - t, 2, 1, K.maroon[3]!);
  p.px.rect(x, y - 28, 2, 2, M.gold[4]!);
  p.px.rect(x + 2, y - 25, 1, 5, M.red[3]!);
  return fin(p);
}

export type PropId =
  | PropId2
  | PropId3
  | PropId4
  | PropIdCarpet
  | 'lanternPost'
  | 'stoneLantern'
  | 'bannerRed'
  | 'bannerBlue'
  | 'bannerJade'
  | 'bannerGold'
  | 'bannerWhite'
  | 'bannerBlack'
  | 'weaponRack'
  | 'dummy'
  | 'dummyStraw'
  | 'mat'
  | 'guardStand'
  | 'brazier'
  | 'gatePost'
  | 'warn1'
  | 'warn2'
  | 'warn3'
  | 'warn4'
  | 'warn5'
  | 'chest'
  | 'well'
  | 'cart'
  | 'fenceX'
  | 'fenceY'
  | 'incense'
  | 'toys'
  | 'hopscotch'
  | 'washSteps'
  | 'dryingLine'
  | 'fishing'
  | 'bench'
  | 'hitchRail'
  | 'hayBales'
  | 'hayPile'
  | 'goods'
  | 'signpost'
  | 'swordStone'
  | 'swordStoneEmpty';

const MAKERS: Record<PropId, () => Sprite> = {
  ...MAKERS2,
  ...MAKERS3,
  ...MAKERS4,
  ...MAKERS_CARPET,
  lanternPost,
  stoneLantern,
  bannerRed: () => bannerPole(M.red, M.gold),
  bannerBlue: () => bannerPole(K.indigo, M.gold),
  bannerJade: () => bannerPole(M.jade, M.gold),
  bannerGold: () => bannerPole(K.ochre, M.red),
  bannerWhite: () => bannerPole(M.linen, M.red),
  bannerBlack: () => bannerPole(M.dark, M.gold),
  weaponRack,
  dummy: () => dummy(1),
  dummyStraw: () => dummy(0),
  mat,
  guardStand,
  brazier: brazierProp,
  gatePost,
  warn1: () => warnSign(1),
  warn2: () => warnSign(2),
  warn3: () => warnSign(3),
  warn4: () => warnSign(4),
  warn5: () => warnSign(5),
  chest,
  well,
  cart,
  fenceX: () => fence('x'),
  fenceY: () => fence('y'),
  incense: incenseBurner,
  toys: () => toys(0),
  hopscotch: () => toys(1),
  washSteps,
  dryingLine,
  fishing,
  bench,
  hitchRail,
  hayBales: () => hay(0),
  hayPile: () => hay(1),
  goods: goodsStack,
  signpost,
  swordStone: () => swordStone(false),
  swordStoneEmpty: () => swordStone(true),
};

const made = new Map<PropId, Sprite>();
/** A prop's sprite, made once. */
export function propSprite(id: PropId): Sprite {
  let s = made.get(id);
  if (!s) made.set(id, (s = MAKERS[id]()));
  return s;
}

/** Review sheets: every prop. */
export function propSheet(): Record<string, Sprite> {
  return Object.fromEntries((Object.keys(MAKERS) as PropId[]).map((k) => [k, propSprite(k)]));
}
